import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { AreaId } from './types/pulse';
import { followUpResult, followUps, openQuestion, openResult, opening, ranking, teamRating, techRating } from './data/pulse';
import { usePulse } from './hooks/usePulse';
import { PulseFrame } from './components/pulse/PulseFrame';
import { DataOverlay } from './components/pulse/DataOverlay';
import { StrategyOverlay } from './components/pulse/StrategyOverlay';
import { OpeningScreen } from './components/pulse/screens/OpeningScreen';
import { RankScreen } from './components/pulse/screens/RankScreen';
import { RankResultScreen } from './components/pulse/screens/RankResultScreen';
import { FollowUpScreen } from './components/pulse/screens/FollowUpScreen';
import { FollowUpResultScreen } from './components/pulse/screens/FollowUpResultScreen';
import { RatingScreen } from './components/pulse/screens/RatingScreen';
import { RatingResultScreen } from './components/pulse/screens/RatingResultScreen';
import { OpenScreen } from './components/pulse/screens/OpenScreen';
import { OpenResultScreen } from './components/pulse/screens/OpenResultScreen';
import { SnapshotScreen } from './components/pulse/screens/SnapshotScreen';
type StepId = 'opening' | 'q1' | 'q1r' | 'q2' | 'q2r' | 'q3' | 'q3r' | 'q4' | 'q4r' | 'q5' | 'q5r' | 'snapshot';
const STEPS: StepId[] = ['opening', 'q1', 'q1r', 'q2', 'q2r', 'q3', 'q3r', 'q4', 'q4r', 'q5', 'q5r', 'snapshot'];
const QUESTION_OF: Record<StepId, number> = {
  opening: 0,
  q1: 1,
  q1r: 1,
  q2: 2,
  q2r: 2,
  q3: 3,
  q3r: 3,
  q4: 4,
  q4r: 4,
  q5: 5,
  q5r: 5,
  snapshot: 6
};
const EASE = [0.23, 1, 0.32, 1] as const;
interface AppProps {
  /** Internal/AbbVie build — adds the AI interpretation to the live results. */
  internalView?: boolean;
}
export function App({
  internalView = true
}: AppProps) {
  const pulse = usePulse();
  const {
    response,
    aggregates
  } = pulse;
  const [index, setIndex] = useState(0);
  const [rankDraft, setRankDraft] = useState<(AreaId | null)[]>([null, null, null]);
  const [choiceDraft, setChoiceDraft] = useState<string | null>(null);
  const [freeDraft, setFreeDraft] = useState('');
  const [teamDraft, setTeamDraft] = useState<number | null>(null);
  const [techDraft, setTechDraft] = useState<number | null>(null);
  const [openDraft, setOpenDraft] = useState('');
  const [dataOpen, setDataOpen] = useState(false);
  const [strategyOpen, setStrategyOpen] = useState(false);
  const step = STEPS[index];
  const area: AreaId = response.topArea ?? 'diagnosis';
  const freeSelected = choiceDraft ? followUps[area].options.find(o => o.id === choiceDraft)?.free === true : false;
  const go = (delta: number) => setIndex(i => Math.min(STEPS.length - 1, Math.max(0, i + delta)));
  const restart = () => {
    pulse.reset();
    setRankDraft([null, null, null]);
    setChoiceDraft(null);
    setFreeDraft('');
    setTeamDraft(null);
    setTechDraft(null);
    setOpenDraft('');
    setIndex(0);
  };
  const commitAndAdvance = () => {
    if (step === 'q1') {
      // A different #1 area routes to a different follow-up question.
      if (rankDraft[0] !== response.topArea) {
        setChoiceDraft(null);
        setFreeDraft('');
      }
      pulse.submitRanking(rankDraft.filter((a): a is AreaId => a !== null));
    }
    if (step === 'q2' && choiceDraft) pulse.submitFollowUp(choiceDraft, freeDraft);
    if (step === 'q3' && teamDraft !== null) pulse.submitRating('team', teamDraft);
    if (step === 'q4' && techDraft !== null) pulse.submitRating('tech', techDraft);
    if (step === 'q5') pulse.submitOpen(openDraft);
    go(1);
  };
  const nextLabel: Record<StepId, string> = {
    opening: opening.cta,
    q1: ranking.cta,
    q1r: ranking.resultCta,
    q2: followUps[area].cta,
    q2r: followUpResult.cta,
    q3: teamRating.cta,
    q3r: teamRating.resultCta,
    q4: techRating.cta,
    q4r: techRating.resultCta,
    q5: openQuestion.cta,
    q5r: openResult.cta,
    snapshot: ''
  };
  const nextDisabled = step === 'q1' && rankDraft.some(a => a === null) || step === 'q2' && (!choiceDraft || freeSelected && !freeDraft.trim()) || step === 'q3' && teamDraft === null || step === 'q4' && techDraft === null || step === 'q5' && !openDraft.trim();
  const footnote = step === 'q5' ? 'Stored anonymously with a timestamp. No name, email or hospital is collected.' : undefined;
  return <div className="min-h-full w-full bg-mist font-sans" data-id="element-0" data-mp="14wnayuekcvrr">
      <main data-id="element-1" data-mp="14v3vh60qeor1">
        <PulseFrame question={QUESTION_OF[step]} totalQuestions={5} footnote={footnote} onBack={index > 0 && step !== 'snapshot' ? () => go(-1) : undefined} onNext={step === 'snapshot' ? undefined : commitAndAdvance} nextLabel={nextLabel[step]} nextDisabled={nextDisabled} onRestart={step === 'opening' ? undefined : restart} onOpenData={() => setDataOpen(true)} onOpenStrategy={internalView ? () => setStrategyOpen(true) : undefined} data-id="element-2" data-mp="zxk3z4f0wfh5">
          <AnimatePresence mode="wait" data-id="element-3" data-mp="206ti8wi15r92">
            <motion.div key={step} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.22,
            ease: EASE
          }} className="flex flex-1 flex-col" data-id="element-4" data-mp="2553hbi6vlbzy">
              {step === 'opening' && <OpeningScreen data-id="element-5" data-mp="2jtrchnct7jpd" />}

              {step === 'q1' && <RankScreen value={rankDraft} onChange={setRankDraft} data-id="element-6" data-mp="2js6efnradimr" />}

              {step === 'q1r' && response.topArea && <RankResultScreen topArea={response.topArea} aggregates={aggregates} data-id="element-7" data-mp="2supey17lnj5u" />}

              {step === 'q2' && <FollowUpScreen area={area} selected={choiceDraft} onSelect={setChoiceDraft} freeText={freeDraft} onFreeText={setFreeDraft} data-id="element-8" data-mp="timzr360daxz" />}

              {step === 'q2r' && response.followUpChoice && <FollowUpResultScreen area={area} choice={response.followUpChoice} freeText={response.followUpFreeText} aggregates={aggregates} data-id="element-9" data-mp="1wubw3j7sls5r" />}

              {step === 'q3' && <RatingScreen block={teamRating} value={teamDraft} onChange={setTeamDraft} data-id="element-10" data-mp="2kp54au7fz0t7" />}

              {step === 'q3r' && response.teamRating !== null && <RatingResultScreen value={response.teamRating} tally={aggregates.team} context="Better use of the wider eye-care team" data-id="element-11" data-mp="2tj00vduhzix8" />}

              {step === 'q4' && <RatingScreen block={techRating} value={techDraft} onChange={setTechDraft} data-id="element-12" data-mp="33el5zkuv4zb7" />}

              {step === 'q4r' && response.techRating !== null && <RatingResultScreen value={response.techRating} tally={aggregates.tech} context="Potential of technology to free specialist time" data-id="element-13" data-mp="3i4ef4zlcn0fn" />}

              {step === 'q5' && <OpenScreen value={openDraft} onChange={setOpenDraft} aggregates={aggregates} data-id="element-14" data-mp="3d3igp1jro6hm" />}

              {step === 'q5r' && <OpenResultScreen aggregates={aggregates} answer={response.openResponse} data-id="element-15" data-mp="2zbajj8okmdie" />}

              {step === 'snapshot' && <SnapshotScreen aggregates={aggregates} onRestart={restart} onOpenData={() => setDataOpen(true)} data-id="element-16" data-mp="2pg0hwm234855" />}
            </motion.div>
          </AnimatePresence>
        </PulseFrame>
      </main>

      <AnimatePresence data-id="element-17" data-mp="2kg4cqjhniao0">
        {dataOpen && <DataOverlay aggregates={aggregates} onClose={() => setDataOpen(false)} internalView={internalView} data-id="element-18" data-mp="2khyyu14ns8to" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-19" data-mp="2vx289t9kyejq">
        {strategyOpen && <StrategyOverlay onClose={() => setStrategyOpen(false)} data-id="element-20" data-mp="2qwxuwc3yxzxq" />}
      </AnimatePresence>
    </div>;
}